import { CLIMATE, TERRAIN, BIOMES } from '../config';
import { valueNoise, clamp01 } from './noise';
import { terrainHeight, terrainSlope } from './terrain';
import { tempField, moistField, irradField } from './climate';

// The single source of truth for "what biome is at (x,z)". Everything that varies by biome (ground
// colour, grass/tree species, atmosphere) reads biomeAt or the climate fields below, the same way every
// ground consumer already reads terrainHeight. The fields are cheap low-frequency noise on independent
// seed lanes, so temperature, moisture, forest density and the radioactive mask vary independently and
// form large coherent regions. Height stays the SHAPE of the land (terrain.ts); biomes PAINT it.

// Order MUST match config.ts BIOMES[] (it is indexed by these numeric values).
export enum Biome {
  Ocean, Beach, Grassland, Forest, Meadow, Desert, Rocky, Hills, Mountain, Snow, Irradiated,
}
export const BIOME_COUNT = 11;

// 0 cold .. 1 hot. The noise lane is quantile-equalized per seed (engine/climate.ts) so every seed gets
// the full, evenly-proportioned hot..cold spread -- no more one-lobe all-tundra/all-desert islands.
// Cooled by altitude (snowy peaks fall out for free) and by a gentle latitude gradient.
export function temperature(x: number, z: number, h = 0): number {
  let t = tempField(x, z);
  t -= (Math.abs(z) / CLIMATE.latSpan) * CLIMATE.latWeight;
  t -= Math.max(0, h - TERRAIN.seaLevel) * CLIMATE.altCooling;
  return clamp01(t);
}

// 0 dry .. 1 wet. Same per-seed equalization as temperature.
export function moisture(x: number, z: number): number {
  return moistField(x, z);
}

// 0..1 woodland density: big soft pockets (~150m) with a finer layer for ragged edges. Byte-identical to
// the old private field in nodes.ts (seed 0), so existing tree placement is unchanged. Trees, bushes and
// the "darker grass = dense forest" tint all read this one field.
export function forestDensity(x: number, z: number): number {
  const s = 0.0009; // big forest regions (scaled with the cell); the finer layer ragged-edges them
  return valueNoise(x * s, z * s, 0) * 0.7 + valueNoise(x * s * 2.7 + 50, z * s * 2.7 - 30, 0) * 0.3;
}

// 0..1 radioactive blight field, quantile-equalized per seed (engine/climate.ts): the top fraction
// above CLIMATE.irradThresh becomes the Irradiated biome, so it is ALWAYS a few rare patches -- the raw
// lane's island-sized lobe used to blight most of the map on unlucky seeds.
export function irradiated(x: number, z: number): number {
  return irradField(x, z);
}

// The dominant biome at a world point. A guard cascade matching how terrainHeight already special-cases
// shore -> snow -> seabed: water/beach by height, then the rare irradiated mask, then elevation bands
// (snow/mountain/hills/rocky), then the lowland temperature x moisture table. Used CPU-side for scatter
// decisions (which tree species, ore bias); ground-colour blending is done shader-side from these fields.
export function biomeAt(x: number, z: number, h?: number): Biome {
  const height = h ?? terrainHeight(x, z);
  const sea = TERRAIN.seaLevel;
  if (height < sea - 0.5) return Biome.Ocean;
  if (height < sea + TERRAIN.beachWidth * 0.6) return Biome.Beach;
  if (irradiated(x, z) > CLIMATE.irradThresh) return Biome.Irradiated;

  const temp = temperature(x, z, height);
  const moist = moisture(x, z);

  // High ground: very high OR cold-region peaks are Snow; otherwise Mountain (snow-capped at the top via
  // the shader's snow term). Use the REGION climate (temp at sea level), not the altitude-cooled value,
  // so a warm-region peak stays Mountain instead of every peak going Snow.
  if (height > TERRAIN.snowStart) {
    const regionTemp = temperature(x, z, sea);
    return (height > TERRAIN.snowEnd || regionTemp < 0.33) ? Biome.Snow : Biome.Mountain;
  }

  // Mid ground: steep = Rocky, otherwise Hills.
  const slope = terrainSlope(x, z, 1.2);
  if (slope > 0.7) return Biome.Rocky;
  if (height > TERRAIN.snowStart * 0.55) return Biome.Hills;

  // Lowland temperature x moisture. Thresholds are quantiles now (the equalized lanes are uniform 0..1):
  // desert ~ the hottest 30% x driest 36%, tundra ~ the coldest 14% (plus the latitude/altitude shifts).
  if (temp > 0.70 && moist < 0.36) return Biome.Desert;
  if (moist > 0.38 && forestDensity(x, z) > CLIMATE.forestThresh) return Biome.Forest;
  if (moist > 0.42) return Biome.Meadow;
  if (temp < 0.14) return Biome.Snow; // cold lowland tundra
  return Biome.Grassland;
}

// --- ground colour (Phase C) ---

// sRGB hex -> linear RGB (the terrain/grass shaders work in linear space). Precomputed once per biome so
// the per-vertex tint below is just a few lerps, no pow().
function s2l(c: number): number { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
function hexLin(hex: number): [number, number, number] {
  return [s2l(((hex >> 16) & 255) / 255), s2l(((hex >> 8) & 255) / 255), s2l((hex & 255) / 255)];
}
const GRASS_LIN = BIOMES.map((b) => hexLin(b.grass));
const GRASS_DENSE_LIN = BIOMES.map((b) => hexLin(b.grassDense));

// The biome's base ground (grass) colour at a point, written as linear RGB into out[o..o+2]. This is what
// the terrain chunk mesh stores per vertex and the splat shader interpolates, so biome borders cross-fade
// smoothly with no GLSL noise needed. Slope-free: the shader still paints dirt/rock on steep ground and
// snow caps by height, so this only needs the climate-driven grass tone. Darker grass = denser forest.
// Slope-free biome pick (shared by ground colour + grass-allowed). The shader paints rock/dirt on
// slopes and snow caps by height, so the colour/cover layers do not need the slope-based Rocky branch.
function biomeNoSlope(x: number, z: number, h: number): Biome {
  const sea = TERRAIN.seaLevel;
  if (h < sea + TERRAIN.beachWidth * 0.6) return Biome.Beach;
  if (irradiated(x, z) > CLIMATE.irradThresh) return Biome.Irradiated;
  if (h > TERRAIN.snowStart) return (h > TERRAIN.snowEnd || temperature(x, z, sea) < 0.33) ? Biome.Snow : Biome.Mountain;
  if (h > TERRAIN.snowStart * 0.55) return Biome.Hills;
  const temp = temperature(x, z, h), moist = moisture(x, z);
  if (temp > 0.70 && moist < 0.36) return Biome.Desert;
  if (moist > 0.38 && forestDensity(x, z) > CLIMATE.forestThresh) return Biome.Forest;
  if (moist > 0.42) return Biome.Meadow;
  if (temp < 0.14) return Biome.Snow;
  return Biome.Grassland;
}

// Per-biome grass DENSITY multiplier (0..1): 0 where the ground is sand/rock/snow/pebbles (desert, rocky,
// snow, irradiated, beach), else the biome's grassDensity (green biomes ~0.85, dirt-ish hills ~0.4, sparse
// mountain ~0.2). Sampled coarsely by the grass field and bilinear-blended so blades thin out across the
// border instead of cutting off.
export function grassDensityAt(x: number, z: number, h: number): number {
  const b = BIOMES[biomeNoSlope(x, z, h)];
  return b.groundCover === 'grass' ? b.grassDensity : 0;
}

// Forest "lushness" 0..1 that drives the grass blade SIZE in the green lowlands. The categorical biome pick
// flips Forest (big blades) to Grassland (small) right at the forestThresh contour, which reads as a HARD line
// in the grass. Instead, ramp the size CONTINUOUSLY across a wide band, fractured by two extra noise scales so
// the tall forest grass fades into the short meadow grass in scattered patches/fingers -- never a clean seam.
// The jitter amplitude is on the order of the ramp half-width, so the boundary breaks into clumps, not an edge.
function forestLush(x: number, z: number): number {
  const fd = forestDensity(x, z);
  const jitter = (valueNoise(x * 0.0055 + 17, z * 0.0055 - 23, 0) - 0.5) * 0.20  // broad bays/peninsulas (~180m)
               + (valueNoise(x * 0.018 + 91, z * 0.018 + 44, 0) - 0.5) * 0.13;   // finer clumps/fraying (~55m)
  let L = clamp01((fd + jitter - (CLIMATE.forestThresh - 0.12)) / 0.24);          // ~0.24-wide ramp around the threshold
  L = L * L * (3 - 2 * L);                                                        // smootherstep ease
  // Track moisture too, so the tall grass actually fills the moist forest (where the trees are) and fades out
  // as the ground dries -- another natural, smooth break-up rather than tall grass on bare dry ground.
  let wet = clamp01((moisture(x, z) - 0.30) / 0.16);
  wet = wet * wet * (3 - 2 * wet);
  return L * wet;
}

// Whether this point is one of the green lowland biomes where the forest tall-grass fade applies (everything
// else -- desert/hills/mountain/snow/rocky/beach/irradiated -- keeps its own categorical blade size).
function isGreenLowland(b: Biome): boolean {
  return b === Biome.Forest || b === Biome.Grassland || b === Biome.Meadow;
}

// Per-biome grass blade SIZE multiplier (1 = default, 2 = forest's taller/wider blades). In the green lowlands
// it ramps continuously (forestLush) so blade size cross-fades in patches at the forest border; other biomes
// keep their fixed value. Sampled + bilinear-blended by the grass field on top of this.
export function grassBladeScale(x: number, z: number, h: number): number {
  const bi = biomeNoSlope(x, z, h);
  if (!isGreenLowland(bi)) return BIOMES[bi].bladeScale;
  return 1 + (BIOMES[Biome.Forest].bladeScale - 1) * forestLush(x, z); // 1 (open grass) .. 2 (forest), smooth + patchy
}

// Per-biome EXTRA grass height multiplier on top of bladeScale (default 1; forest 1.6 = tall grass). Same
// continuous forest fade in the green lowlands; other biomes (e.g. desert's tall dry grass) keep their value.
export function grassBladeTall(x: number, z: number, h: number): number {
  const bi = biomeNoSlope(x, z, h);
  if (!isGreenLowland(bi)) return BIOMES[bi].bladeTall ?? 1;
  return 1 + ((BIOMES[Biome.Forest].bladeTall ?? 1) - 1) * forestLush(x, z); // 1 .. 1.6, smooth + patchy
}

// Per-biome grass-blade tint (linear RGB) written into out[o..o+2]. (0,0,0) means "use the default green
// gradient" (every green biome); desert/irradiated set a colour so their blades read dry / toxic, not green.
const GRASS_TINT_LIN = BIOMES.map((b) => (b.grassTint ? hexLin(b.grassTint) : [0, 0, 0] as [number, number, number]));

// BIOME COLOUR BLEND. The per-biome ground/grass colour is picked from a DISCRETE biome, so it SNAPS at the
// classification thresholds -> a hard seam in the ground + grass. Soften it by AVERAGING the colour over a ring
// around the point, so a border cross-fades across ~2x the ring radius instead of a clean line. The offsets use
// the point's own height (cheap: no extra terrainHeight), so the temp/moisture-driven LOWLAND borders (the
// visible green<->dry seam) blend; height borders (beach/snow line) still resolve by the shader's own layers.
// Bake-time only (per terrain vertex + grass-grid cell), so a handful of extra samples per point is fine.
const _tmpCol = new Float32Array(3);
const BLEND_R = 26; // metres; a border fades across ~2x this (~52m)
const BLEND_OFF: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [BLEND_R, 0], [-BLEND_R, 0],
  [BLEND_R * 0.5, BLEND_R * 0.866], [-BLEND_R * 0.5, BLEND_R * 0.866],
  [BLEND_R * 0.5, -BLEND_R * 0.866], [-BLEND_R * 0.5, -BLEND_R * 0.866],
];

function grassTintRaw(x: number, z: number, h: number, out: Float32Array, o: number): void {
  const t = GRASS_TINT_LIN[biomeNoSlope(x, z, h)];
  out[o] = t[0]; out[o + 1] = t[1]; out[o + 2] = t[2];
}
export function grassTintAt(x: number, z: number, h: number, out: Float32Array, o: number): void {
  let r = 0, g = 0, b = 0;
  for (const off of BLEND_OFF) { grassTintRaw(x + off[0], z + off[1], h, _tmpCol, 0); r += _tmpCol[0]; g += _tmpCol[1]; b += _tmpCol[2]; }
  const n = BLEND_OFF.length; out[o] = r / n; out[o + 1] = g / n; out[o + 2] = b / n;
}

function groundColorRaw(x: number, z: number, h: number, out: Float32Array, o: number): void {
  const b = biomeNoSlope(x, z, h);
  const fd = clamp01((forestDensity(x, z) - 0.3) * 2.0); // 0..1 forest darkening
  const g = GRASS_LIN[b], gd = GRASS_DENSE_LIN[b];
  out[o] = g[0] + (gd[0] - g[0]) * fd;
  out[o + 1] = g[1] + (gd[1] - g[1]) * fd;
  out[o + 2] = g[2] + (gd[2] - g[2]) * fd;
}
export function groundTint(x: number, z: number, h: number, out: Float32Array, o: number): void {
  let r = 0, g = 0, b = 0;
  for (const off of BLEND_OFF) { groundColorRaw(x + off[0], z + off[1], h, _tmpCol, 0); r += _tmpCol[0]; g += _tmpCol[1]; b += _tmpCol[2]; }
  const n = BLEND_OFF.length; out[o] = r / n; out[o + 1] = g / n; out[o + 2] = b / n;
}
